import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, FilePlus2, Inbox, Users, Package, Building2, Briefcase, Settings, LogOut,
  KeyRound, Menu, Archive, BookUser, Boxes, UserCog, MailPlus, FolderOpen, Truck, Warehouse, Receipt, Wallet, ClipboardList, Percent, FileSignature, BarChart3, BookOpen, FileClock, HardDrive,
} from "lucide-react";
import { BrandMark } from "@/components/LogoUploader";
import { useBranding } from "@/lib/branding";
import { useAuth, isAdmin, isAgent } from "@/context/AuthContext";
import { RUOLI, api, isProfessionista } from "@/lib/api";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { BillingSwitch } from "@/components/BillingSwitch";
import { NotificheBell } from "@/components/NotificheBell";
import { PackageAlert } from "@/components/PackageAlert";

function navFor(u, mag, corr, agenti, pn, pro, prof) {
  if (u.role === "superadmin")
    return [
      ["Panoramica", "/admin", LayoutDashboard, true],
      ["Pacchetti XML", "/admin/pacchetti", Package],
      ["Spazio usato", "/admin/spazio", HardDrive],
      ["Studi", "/admin/studi", Briefcase],
      ["Aziende clienti", "/admin/aziende", Building2],
      ["Utenti", "/admin/utenti", Users],
      ["Richieste", "/admin/richieste", MailPlus],
      ["Visite al sito", "/admin/visite", BarChart3],
      ["Manuali", "/admin/manuali", BookOpen],
    ];
  if (u.role === "studio")
    return [
      ["Aziende abbinate", "/studio", Building2, true],
      ["Archivio XML", "/studio/archivio", Archive],
      ["Documenti", "/studio/documenti", FolderOpen],
      ["Contabilità clienti", "/studio/primanota", BookOpen],
      ...(isAdmin(u) && !u.demo ? [["Utenti studio", "/studio/utenti", Users]] : []),
    ];
  if (isAgent(u))
    return [
      ["Incassi", "/app/incassi", Wallet],
      ["Preventivi", "/app/preventivi", FileSignature],
      ["Ordini", "/app/ordini", ClipboardList],
      ["Documenti di trasporto", "/app/ddt", Truck],
      ["Clienti", "/app/anagrafiche", BookUser],
      ["Catalogo e giacenze", "/app/catalogo", Boxes],
      ["Le mie provvigioni", "/app/provvigioni", Percent],
    ];
  return [
    ["Dashboard", "/app", LayoutDashboard, true],
    ["Nuova fattura", "/app/fatture/nuova", FilePlus2],
    ["Fatture emesse", "/app/fatture", FileText],
    ["Fatture ricevute", "/app/ricevute", Inbox],
    ["Incassi", "/app/incassi", Wallet],
    ["Preventivi", "/app/preventivi", FileSignature],
    ...(pro ? [["Fatture proforma", "/app/proforma", FileClock]] : []),
    ...(agenti ? [["Ordini clienti", "/app/ordini", ClipboardList]] : []),
    ...(!prof || agenti ? [["Documenti di trasporto", "/app/ddt", Truck]] : []),
    ["Clienti e fornitori", "/app/anagrafiche", BookUser],
    ["Articoli e servizi", "/app/articoli", Boxes],
    ...(mag ? [["Magazzino fiscale", "/app/magazzino", Warehouse]] : []),
    ...(corr ? [["Corrispettivi", "/app/corrispettivi", Receipt]] : []),
    ...(pn ? [["Contabilità", "/app/primanota", BookOpen]] : []),
    ...(agenti ? [["Provvigioni agenti", "/app/provvigioni", Percent]] : []),
    ["Documenti", "/app/documenti", FolderOpen],
    ...(isAdmin(u) ? [["Impostazioni", "/app/impostazioni", Settings]] : []),
    ...(isAdmin(u) && !u.demo ? [["Utenti", "/app/utenti", UserCog]] : []),
  ];
}

const TrialBanner = ({ user }) => {
  const days = Math.ceil((new Date(`${user.trial_fine}T23:59:59`) - new Date()) / 86400000);
  return user.trial_scaduta ? (
    <div data-testid="trial-banner-expired" className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
      <b>Prova gratuita scaduta.</b> L'account è in sola lettura: puoi consultare e scaricare i dati. Per attivare il piano scrivi a <a className="underline" href="mailto:clienti@fatturazionegestionale.com">clienti@fatturazionegestionale.com</a>.
    </div>
  ) : (
    <div data-testid="trial-banner" className="mb-6 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
      <span><b>Prova gratuita:</b> {days} {days === 1 ? "giorno rimasto" : "giorni rimasti"} (fino al {user.trial_fine.split("-").reverse().join("/")}). L'invio allo SdI si attiva con il piano.</span>
      <a className="font-semibold underline" href="mailto:clienti@fatturazionegestionale.com?subject=Attivazione%20piano">Attiva il piano</a>
    </div>
  );
};

const ROLE_LABEL = { superadmin: "Studio Principale", studio: "Studio", company: "Azienda" };

function Sidebar({ user, onNav }) {
  const { logo } = useBranding();
  const [mag, setMag] = useState(false);
  const [corr, setCorr] = useState(false);
  const [agenti, setAgenti] = useState(false);
  const [pn, setPn] = useState(false);
  const [pro, setPro] = useState(false);
  const [prof, setProf] = useState(false);
  useEffect(() => {
    if (user.role === "company" && user.tenant_role !== "agente") api.get("/primanota/stato").then((r) => setPn(r.data.abilitato)).catch(() => {});
    if (user.role === "company") api.get("/magazzino/stato").then((r) => setMag(r.data.attivo)).catch(() => {});
    if (user.role === "company" && user.tenant_role !== "agente") api.get("/corrispettivi/stato").then((r) => setCorr(r.data.attivo)).catch(() => {});
    if (user.role === "company" && user.tenant_role !== "agente") api.get("/company").then((r) => { const p = isProfessionista(r.data); setAgenti(!!r.data.agenti_attivo); setProf(p); setPro(p || !!r.data.agenti_attivo); }).catch(() => {});
  }, [user.role, user.company_id, user.tenant_role]);
  return (
    <div className="flex h-full flex-col border-r border-[#1E293B] bg-[#020617] text-slate-100">
      <div className="pb-6 pl-5 pr-12 pt-7 md:pr-5">
        <BrandMark logo={logo} />
        <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-slate-300">Gestionale e fatturazione</div>
      </div>
      <div className="mx-4 mb-5 flex items-center gap-3 rounded-lg border border-[#334155] bg-[#1E293B] px-3 py-2.5">
        {user.scope_logo && <img src={user.scope_logo} alt="" data-testid="sidebar-scope-logo" className="h-9 w-9 shrink-0 rounded bg-white object-contain p-0.5" />}
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-sky-300">{ROLE_LABEL[user.role]}</div>
          <div data-testid="sidebar-scope-name" className="truncate text-sm font-semibold text-white">{user.scope_name}</div>
        </div>
      </div>
      <nav className="flex-1 space-y-0.5 px-3">
        {navFor(user, mag, corr, agenti, pn, pro, prof).map(([label, to, Icon, end]) => (
          <NavLink
            key={to}
            to={to}
            end={!!end || to === "/app/fatture"}
            onClick={onNav}
            data-testid={`nav-${to.replace(/\//g, "-").slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-150 ${
                isActive ? "bg-white font-semibold text-[#020617] shadow-sm" : "hover:bg-[#1E293B] hover:text-white"
              }`
            }
          >
            <Icon className="h-4 w-4" strokeWidth={1.75} />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="px-6 py-5 text-[11px] text-slate-300">FatturaPA v1.2.2 · SdI<br />© Generservice srls</div>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState(false);

  return (
    <div className="flex min-h-screen bg-slate-100">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 md:block">
        <Sidebar user={user} />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 border-0 p-0">
          <Sidebar user={user} onNav={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md sm:px-8">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="hidden text-sm text-slate-500 md:block">{user.scope_name}</div>
          <div className="ml-auto mr-3 flex items-center gap-2">
            {user.demo && <span data-testid="demo-badge" className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">DEMO · dati di esempio ripristinati ogni notte</span>}
            {user.company_id && <NotificheBell />}
            <BillingSwitch />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button" className="flex items-center gap-3 rounded-full py-1 pl-1 pr-3 transition-colors hover:bg-slate-100">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                  {(user.name || user.email).slice(0, 2).toUpperCase()}
                </span>
                <span className="hidden text-left sm:block">
                  <span className="block text-sm font-semibold leading-tight text-slate-800">{user.name}</span>
                  <span className="block text-[11px] leading-tight text-slate-500">{user.role === "superadmin" ? "Super amministratore" : user.avatar ? "Studio Principale · modalità Avatar" : user.acting_studio ? "Studio · la tua azienda" : user.acting_superadmin ? "Studio Principale · fatturazione" : RUOLI[user.tenant_role]}</span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="truncate text-xs font-normal text-slate-500">{user.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {!user.demo && (
                <DropdownMenuItem data-testid="change-password-menu" onClick={() => setPwd(true)}>
                  <KeyRound className="mr-2 h-4 w-4" /> Cambia password
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="logout-button" onClick={async () => { await logout(); nav("/login"); }}>
                <LogOut className="mr-2 h-4 w-4" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="mx-auto w-full max-w-[1400px] flex-1 p-4 sm:p-6 lg:p-10">
          {user.acting_studio && (
            <div data-testid="studio-billing-banner" className="mb-6 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              <span><b>Stai gestendo la tua azienda</b> con l'accesso dello studio.</span>
              <button type="button" data-testid="studio-billing-exit" className="rounded-md bg-emerald-700 px-3 py-1.5 font-semibold text-white hover:bg-emerald-800"
                onClick={async () => { await api.post("/auth/billing-mode", { on: false }); window.location.href = "/studio"; }}>Torna allo studio</button>
            </div>
          )}
          {user.avatar && (
            <div data-testid="avatar-banner" className="mb-6 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              <span><b>Modalità Avatar:</b> stai operando come {user.avatar_name}. Accesso autorizzato dall'azienda, registrato, max 2 ore.</span>
              <button type="button" data-testid="avatar-exit-button" className="rounded-md bg-emerald-700 px-3 py-1.5 font-semibold text-white hover:bg-emerald-800"
                onClick={async () => { await api.post("/auth/avatar-exit"); window.location.href = "/admin"; }}>Esci da Avatar</button>
            </div>
          )}
          {user.trial && <TrialBanner user={user} />}
          {user.role === "company" && user.tenant_role !== "agente" && !user.demo && <PackageAlert user={user} />}
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog open={pwd} onOpenChange={setPwd} />
    </div>
  );
}
